import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, Button, Radio } from 'react-bootstrap';
import { set, cloneDeep, isNull } from 'lodash';
import { Image } from '@components/common';
import utils from '@utils';
import FormUtils from '@utils/formUtils';
import BankAccountForm from './BankAccountForm';
import CreditCardForm from './CreditCardForm';
import PlaidWidget from './PlaidWidget';

class PaymentMethodsForm extends Component {
	constructor(props) {
		super(props);

		this.state = {
			bankAccountForm : props.bankAccountForm,
			form            : {
				type : {
					value      : 'bankAccount',
					validation : null,
					rules      : 'required',
				},
				bankAccount : {
					routingNo : {
						value      : '',
						validation : null,
						rules      : 'required|numeric',
					},
					accountNo : {
						value      : '',
						validation : null,
						rules      : 'required|numeric|confirmed',
					},
					// eslint-disable-next-line camelcase
					accountNo_confirmation : {
						value      : '',
						validation : null,
						rules      : 'required|numeric',
					},
					accountType : {
						value      : '',
						validation : null,
						rules      : 'required',
					},
					bankHolderName : {
						value      : '',
						validation : null,
						rules      : 'required',
					},
				},
				creditCard : {
					cardHolderName : {
						value      : '',
						validation : null,
						rules      : 'required',
					},
					cardNumber : {
						value      : '',
						validation : null,
						rules      : 'required',
					},
					expiryDate : {
						value      : '',
						validation : null,
						rules      : 'required',
					},
					cvc : {
						value      : '',
						validation : null,
						rules      : 'required|numeric',
					},
					address : {
						value      : '',
						validation : null,
						rules      : 'required',
					},
					city : {
						value      : '',
						validation : null,
						rules      : 'required',
					},
					state : {
						value      : '',
						validation : null,
						rules      : 'required',
					},
					zip : {
						value      : '',
						validation : null,
						rules      : 'required|numeric',
					},
				},
			},
		};
	}

	componentDidMount() {
		this.setDefaultBillingAddress();
	}

	setDefaultBillingAddress = () => {
		const {
			userAddress: {
				streetAddress, city, stateId, zip,
			},
		} = this.props;
		const { form: { creditCard } } = this.state;

		const newCreditCardForm = { ...creditCard };
		set(newCreditCardForm, 'address.value', streetAddress || '');
		set(newCreditCardForm, 'city.value', city || '');
		set(newCreditCardForm, 'state.value', stateId || '');
		set(newCreditCardForm, 'zip.value', zip || '');

		this.setState((prevState) => ({
			form : {
				...prevState.form,
				creditCard : newCreditCardForm,
			},
		}));
	}

	submitForm = () => {
		const { form } = this.state;
		const { onSubmit } = this.props;

		// Validate form
		const { isValid, form: validatedForm, values } = FormUtils.validateForm(form[form.type.value]);

		const newState = cloneDeep(this.state);
		set(newState, ['form', form.type.value], validatedForm);

		// Validate credit card
		let isCreditCardValid = true;
		if (form.type.value === 'creditCard') {
			const cleanValue = values.cardNumber.replace(/\s+/g, '');
			isCreditCardValid = utils.processCardNumber(cleanValue).isValid;

			set(newState, 'form.creditCard.cardNumber.validation', isCreditCardValid ? null : 'error');
		}

		this.setState(newState);

		if (isValid && isCreditCardValid) onSubmit({ ...values, type : form.type.value });
		else onSubmit({ error : true });
	}

	handleBankAccountFormChange = (property, type) => (event) => utils.genericOnFieldChangeHandler(`bankAccount.${property}.value`, type, this, event);

	handleCreditCardFormChange = (property, type) => (event) => utils.genericOnFieldChangeHandler(`creditCard.${property}.value`, type, this, event);

	toggleBankForm = () => this.setState((prevState) => ({ bankAccountForm : !prevState.bankAccountForm }));

	handleChange = (property, type) => (event) => utils.genericOnFieldChangeHandler(property, type, this, event);

	render() {
		const { form, bankAccountForm } = this.state;
		const {
			USStates, loadingUSStates, plaidSettings, onCancel, cancelButton, submitButton,
		} = this.props;

		return (
			<div>
				<div>
					<div className="margin-left-15 margin-right-15">
						<div className="margin-bottom-15 padding-top-10">
							<span className="font-bold color-gray-mine-shaft font-size-16">Payment method</span>
							<Image
								className="margin-left-20"
								src="/images/cardImage.png"
							/>
						</div>
						<FormGroup
							controlId="paymentType"
						>
							<div className="flex flex-align-items-center">
								<div>
									<Radio
										name="_paymentType"
										onChange={() => utils.genericOnFieldChangeHandler('type.value', 'value', this, 'bankAccount')}
										checked={form.type.value === 'bankAccount'}
									>
										ACH Bank Transfer
									</Radio>
								</div>
								<div className="margin-left-30">
									<Radio
										name="_paymentType"
										onChange={() => utils.genericOnFieldChangeHandler('type.value', 'value', this, 'creditCard')}
										checked={form.type.value === 'creditCard'}
									>
										Credit Card
									</Radio>
								</div>
							</div>
						</FormGroup>
						<Choose>
							<When condition={form.type.value === 'bankAccount'}>
								<Choose>
									<When condition={bankAccountForm}>
										<BankAccountForm
											form={form.bankAccount}
											onChange={this.handleBankAccountFormChange}
										/>
									</When>
									<Otherwise>
										<div className="text-center">
											<PlaidWidget
												env={plaidSettings.env}
												public_key={plaidSettings.publicKey}
												onSuccess={plaidSettings.onSuccess}
												onExit={plaidSettings.onExit}
											/>
											<p>&nbsp;</p>
											<br />
											<div className="small">
												Problems linking your account?
												<br />
												<button className="btn btn-link" type="button" onClick={this.toggleBankForm}>Manual Account Entry Form</button>
											</div>
										</div>
									</Otherwise>
								</Choose>
							</When>
							<When condition={form.type.value === 'creditCard'}>
								<CreditCardForm
									form={form.creditCard}
									USStates={USStates}
									loadingUSStates={loadingUSStates}
									onChange={this.handleCreditCardFormChange}
								/>
							</When>
						</Choose>
					</div>
				</div>
				<div className="text-right margin-top-30 margin-right-15">
					<FormGroup>
						<If condition={!isNull(cancelButton)}>
							<Button
								bsStyle="link"
								className="margin-right-10"
								onClick={onCancel}
							>
								<If condition={cancelButton.icon}>
									<i className={`margin-right-10 rkf ${cancelButton.icon}`} />
								</If>
								{cancelButton.label}
							</Button>
						</If>
						<If condition={(form.type.value === 'creditCard') || (form.type.value === 'bankAccount' && bankAccountForm)}>
							<Button
								bsStyle="primary"
								className="btn-medium"
								onClick={this.submitForm}
							>
								{submitButton.label}
								<If condition={submitButton.icon}>
									<i className={`margin-left-10 rkf ${submitButton.icon}`} />
								</If>
							</Button>
						</If>

					</FormGroup>
				</div>
			</div>
		);
	}
}

PaymentMethodsForm.propTypes = {
	bankAccountForm : PropTypes.bool,
	USStates        : PropTypes.array,
	loadingUSStates : PropTypes.bool,
	onCancel        : PropTypes.func,
	onSubmit        : PropTypes.func.isRequired,
	cancelButton    : PropTypes.shape({
		label : PropTypes.string,
		icon  : PropTypes.string,
	}),
	submitButton : PropTypes.shape({
		label : PropTypes.string,
		icon  : PropTypes.string,
	}),
	userAddress : PropTypes.shape({
		streetAddress : PropTypes.string,
		city          : PropTypes.string,
		stateId       : PropTypes.number,
		zip           : PropTypes.string,
	}).isRequired,
	plaidSettings : PropTypes.shape({
		env       : PropTypes.string,
		publicKey : PropTypes.string,
		onSuccess : PropTypes.func,
		onExit    : PropTypes.func,
	}).isRequired,
};

PaymentMethodsForm.defaultProps = {
	bankAccountForm : false,
	USStates        : [],
	loadingUSStates : false,
	onCancel        : () => {},
	cancelButton    : { label : 'Cancel', icon : null },
	submitButton    : { label : 'Save', icon : null },
};

export default PaymentMethodsForm;
